import React, { useState } from 'react';
import { HelpCircle, Search, ChevronDown, ChevronUp, Landmark, ShieldCheck, Coins, PiggyBank, MessageSquare, PhoneCall } from 'lucide-react';

export default function FAQ() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [expandedFaq, setExpandedFaq] = useState(null);

  const categories = ['All', 'Membership', 'Deposits', 'Loans', 'Security'];

  const faqs = [
    {
      id: 1,
      category: 'Membership',
      question: 'What is Sahyadri Urban Credit Society?',
      answer: 'Sahyadri Urban Co-operative Credit Society Ltd. is a leading member-owned cooperative financial institution registered under the Co-operative Societies Act in Maharashtra. Established in 2005, we focus on providing secure deposits, crop/MSME credits, and community financial support.',
      icon: Landmark
    },
    {
      id: 2,
      category: 'Membership',
      question: 'How do I become a member and open a Savings Account?',
      answer: 'Any citizen residing in Maharashtra can apply for membership. You can initiate onboarding by clicking "Create Account" on our portal, providing your Aadhaar Card, PAN Card, and registered mobile number, or by visiting any nearby branch with two passport-sized photographs.',
      icon: Landmark
    },
    {
      id: 3,
      category: 'Membership',
      question: 'What are the working hours of the society?',
      answer: 'Our corporate office and branches are open Monday through Saturday, from 10:00 AM to 5:00 PM. We are closed on Sundays, the 2nd and 4th Saturdays of every month, and all bank holidays.',
      icon: Landmark
    },
    {
      id: 4,
      category: 'Deposits',
      question: 'What are the current interest rates offered on Fixed Deposits?',
      answer: 'We offer highly competitive interest rates on term deposits, ranging from 7.50% to 8.25% per annum depending on tenure. Interest payouts can be configured as monthly, quarterly, or cumulative at maturity.',
      icon: PiggyBank
    },
    {
      id: 5,
      category: 'Deposits',
      question: 'Do you offer special interest rates for Senior Citizens?',
      answer: 'Yes! Senior Citizen members (aged 60 and above) receive an additional interest premium of 0.50% on all Fixed Deposit tenures, yielding up to 8.75% per annum on term investments.',
      icon: PiggyBank
    },
    {
      id: 6,
      category: 'Deposits',
      question: 'Can I withdraw my Fixed Deposit prematurely?',
      answer: 'Premature withdrawals are permitted under the terms of the deposit receipt. A minor penalty interest rate charge of 1% is applicable to premature FD liquidations.',
      icon: PiggyBank
    },
    {
      id: 7,
      category: 'Loans',
      question: 'What types of loans does Sahyadri Urban provide?',
      answer: 'We provide various crop, agricultural development, vehicle, business expansion, home, gold, and personal loans. Our credit options feature low interest, flexible repayment terms, and fast processing.',
      icon: Coins
    },
    {
      id: 8,
      category: 'Loans',
      question: 'What is the processing time and document check for a Gold Loan?',
      answer: 'Gold Loans are disbursed within 30 minutes of ornament valuation. You need to submit your KYC documents (Aadhaar or PAN) and sign the pledge agreement at the branch. No complex credit history checks are required.',
      icon: Coins
    },
    {
      id: 9,
      category: 'Security',
      question: 'How secure are my deposits with Sahyadri Co-operative?',
      answer: 'Your savings are highly secure. We operate under strict co-operative registrar guidelines, maintain conservative liquidity ratios, hold our clearing routes with ICICI Bank, and undergo regular external financial audits.',
      icon: ShieldCheck
    },
    {
      id: 10,
      category: 'Security',
      question: 'How do I register for and secure my online Member Portal credentials?',
      answer: 'You can register online by providing your member account number and verification details on the Registration page. Ensure you set a strong passcode, never share OTPs over call or SMS, and change your password every 90 days.',
      icon: ShieldCheck
    }
  ];

  const filteredFaqs = faqs.filter(faq => {
    const matchesSearch = faq.question.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === 'All' || faq.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const toggleFaq = (id) => {
    setExpandedFaq(expandedFaq === id ? null : id);
  };

  return (
    <div className="pt-[68px] font-sans bg-[#F7F9FC]">
      {/* Header Banner */}
      <section 
        className="relative overflow-hidden text-navy py-20 px-4 text-center bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('/page header.png')" }}
      >
        <div className="max-w-4xl mx-auto space-y-4">
          <span className="text-xs font-bold text-[#B31825] uppercase tracking-widest block">Help Center & support</span>
          <h1 className="font-montserrat font-extrabold text-3xl sm:text-4xl text-navy">Frequently Asked Questions</h1>
          <p className="text-slate-600 text-sm sm:text-base font-normal">
            Find immediate answers regarding account onboarding, competitive deposit rates, quick credit facilities, and portal security.
          </p>
        </div>
      </section>

      {/* Main Content Split (References Accordion Layout input_file_4.png) */}
      <section className="py-20 text-navy max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          {/* Left Column - Stylized Help Center Visual Card */}
          <div className="lg:col-span-4 relative sticky top-28 space-y-6">
            <div className="bg-white rounded-3xl p-8 border border-gray-200 shadow-xl space-y-6 overflow-hidden relative">
              <div className="absolute top-0 right-0 w-20 h-20 bg-[#B31825]/5 rounded-bl-full" />
              <div className="space-y-4">
                <span className="text-xs font-bold text-[#B31825] uppercase tracking-widest block">Quick Contact</span>
                <h3 className="font-montserrat font-extrabold text-xl text-navy">Need Custom Support?</h3>
                <p className="text-gray-500 text-xs sm:text-sm leading-relaxed">
                  Our cooperative audit officers and branch managers are ready to assist you directly.
                </p>
              </div>

              {/* Contacts Slots */}
              <div className="space-y-3 pt-2">
                <a 
                  href="tel:18002700222" 
                  className="flex items-center space-x-3.5 bg-neutralBg p-3 rounded-xl border border-gray-100 hover:border-[#B31825] transition-all group"
                >
                  <div className="bg-[#B31825] text-white p-2.5 rounded-lg shadow group-hover:scale-105 transition-transform">
                    <PhoneCall className="h-4 w-4" />
                  </div>
                  <div>
                    <h4 className="font-bold text-xs text-navy uppercase">Toll-Free Helpline</h4>
                    <p className="text-[11px] text-gray-500 font-semibold font-mono">1800-270-0222</p>
                  </div>
                </a>

                <a 
                  href="mailto:support@sahyadriurban.com" 
                  className="flex items-center space-x-3.5 bg-neutralBg p-3 rounded-xl border border-gray-100 hover:border-[#B31825] transition-all group"
                >
                  <div className="bg-[#052046] text-white p-2.5 rounded-lg shadow group-hover:scale-105 transition-transform">
                    <MessageSquare className="h-4 w-4" />
                  </div>
                  <div>
                    <h4 className="font-bold text-xs text-navy uppercase">Email Helpdesk</h4>
                    <p className="text-[11px] text-gray-500 font-semibold font-mono">support@sahyadriurban.com</p>
                  </div>
                </a>
              </div>
            </div>
          </div>

          {/* Right Column - Search, Categories & Accordions */}
          <div className="lg:col-span-8 space-y-8">
            {/* Search Bar */}
            <div className="relative shadow-sm">
              <input
                type="text"
                placeholder="Search FAQs by keywords (e.g. gold loan, interest rate, KYC)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-white border border-gray-200 rounded-2xl pl-12 pr-4 py-4 text-sm text-navy focus:outline-none focus:border-[#B31825] focus:ring-1 focus:ring-[#B31825] transition-all"
              />
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            </div>

            {/* Categories Tabs */}
            <div className="flex flex-wrap gap-2 justify-start select-none">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => { setActiveCategory(cat); setExpandedFaq(null); }}
                  className={`px-5 py-2.5 rounded-xl font-montserrat font-bold text-xs transition-all border ${
                    activeCategory === cat 
                      ? 'bg-[#B31825] border-[#B31825] text-white shadow-md' 
                      : 'bg-white border-gray-200 text-navy hover:bg-gray-50'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Accordion Lists */}
            {filteredFaqs.length > 0 ? (
              <div className="space-y-4">
                {filteredFaqs.map((faq) => {
                  const IconComp = faq.icon;
                  const isExpanded = expandedFaq === faq.id;
                  return (
                    <div 
                      key={faq.id} 
                      className={`bg-white border rounded-3xl transition-all duration-300 overflow-hidden ${
                        isExpanded ? 'border-[#B31825] shadow-md' : 'border-gray-200/70 hover:border-gray-300'
                      }`}
                    >
                      <button
                        onClick={() => toggleFaq(faq.id)}
                        className="w-full flex items-center justify-between p-5 text-left focus:outline-none"
                      >
                        <div className="flex items-start space-x-3.5 pr-4">
                          <div className={`p-2.5 rounded-xl shrink-0 mt-0.5 transition-colors ${
                            isExpanded ? 'bg-[#B31825]/10 text-[#B31825]' : 'bg-neutralBg text-navy'
                          }`}>
                            <IconComp className="h-5 w-5" />
                          </div>
                          <span className="font-montserrat font-bold text-sm sm:text-base text-navy leading-snug">
                            {faq.question}
                          </span>
                        </div>
                        {isExpanded ? (
                          <ChevronUp className="h-5 w-5 text-[#B31825] shrink-0" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-gray-400 shrink-0" />
                        )}
                      </button>
                      
                      {isExpanded && (
                        <div className="px-5 pb-6 pl-14 sm:pl-16 border-t border-gray-50 pt-4 animate-fadeIn">
                          <p className="text-gray-500 text-xs sm:text-sm leading-relaxed font-light">
                            {faq.answer}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16 bg-white border border-gray-200 rounded-3xl space-y-3">
                <HelpCircle className="h-10 w-10 text-gray-300 mx-auto" />
                <h4 className="font-montserrat font-bold text-base text-navy">No FAQ results match your search</h4>
                <p className="text-xs text-gray-500 max-w-sm mx-auto">Try typing another query or clear categories filter to view all questions.</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
